/**
 * @fileoverview Shared callable API contract for `removeTeachBackUpload`
 * (#5270), which removes a learner's uploaded Teach it back video and gives
 * its attempt back. The functions handler and the webapp client both import
 * it, so the two sides cannot drift in name, request shape, or response
 * shape.
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';
import {
  TeachBackAttemptIdSchema,
  TeachBackSectionWeekSchema,
} from './teachBack';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase.
 */
export const REMOVE_TEACH_BACK_UPLOAD_FUNCTION_NAME = 'removeTeachBackUpload';

/** What the client sends: the upload to remove. */
export const RemoveTeachBackUploadRequestSchema = z.object({
  /** The course. */
  courseId: FirestoreDocumentIdSchema,
  /** The 1-based section week. */
  sectionWeek: TeachBackSectionWeekSchema,
  /** The upload's attempt. */
  attemptId: TeachBackAttemptIdSchema,
});

/** Parsed request. */
export type RemoveTeachBackUploadRequest = z.infer<
  typeof RemoveTeachBackUploadRequestSchema
>;

/** Why nothing was removed. */
export type RemoveTeachBackUploadRefusal =
  /** The learner has no attempt with this id for the week. */
  | 'unknown-attempt'
  /** The attempt is a live session's recording, which can't be removed. */
  | 'not-an-upload';

/** What the callable returns. */
export type RemoveTeachBackUploadResponse =
  | {
      removed: true;
      /** Charged attempts once the upload's is given back. */
      attemptsUsed: number;
      /**
       * The week's credited attempt now: unchanged, the newest remaining
       * eligible attempt if the upload was the credit, or null when none is
       * left and the week is no longer credited.
       */
      creditedAttemptId: string | null;
    }
  | {
      removed: false;
      /** Why not. */
      reason: RemoveTeachBackUploadRefusal;
    };
